import { FC } from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faExclamation } from '@fortawesome/pro-solid-svg-icons/faExclamation';
import { faInfo } from '@fortawesome/pro-solid-svg-icons/faInfo';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { cn, FaIcon, TStatus, statuses } from '@fe-learner-common';

type StatusWithoutIcon = Exclude<TStatus, 'unknown'>;

type StatusIconProps = {
  className?: string;
  status: TStatus;
};

type IconStyle = {
  default: string;
  borderedIcon: string;
};

const isStatusWithIcon = (status: TStatus): status is StatusWithoutIcon => {
  return statuses.includes(status);
};

const colorMap: Record<TStatus, IconStyle> = {
  info: {
    default: 'bg-prominence-500',
    borderedIcon: 'border-prominence-50',
  },
  warning: {
    default: 'bg-attention-500',
    borderedIcon: 'border-attention-50',
  },
  positive: {
    default: 'bg-positive-500',
    borderedIcon: 'border-positive-50',
  },
  negative: {
    default: 'bg-negative-500',
    borderedIcon: 'border-negative-50',
  },
  unknown: {
    default: 'bg-neutral-50 border-neutral-400 border-2',
    borderedIcon: 'border-neutral-50 ',
  },
};

const iconMap: Record<StatusWithoutIcon, IconDefinition> = {
  info: faInfo,
  warning: faExclamation,
  positive: faCheck,
  negative: faXmark,
};

export const Icon: FC<StatusIconProps> = ({ status, className }) => {
  const icon = isStatusWithIcon(status) ? iconMap[status] : null;

  return (
    <div
      className={cn(
        'box-content flex h-[20px] w-[20px] items-center justify-center gap-2 rounded-full',
        colorMap[status].default,
        className,
      )}
    >
      {icon && (
        <FaIcon className="text-neutral-0" width={12} height={12} icon={icon} />
      )}
    </div>
  );
};

export const BorderedStatusIcon: FC<StatusIconProps> = ({ status }) => (
  <Icon
    status={status}
    className={cn('h-[24px] w-[24px] border-12', colorMap[status].borderedIcon)}
  />
);
